/**
 * /gift/success — Shown after successful Stripe payment for a gift.
 * No Mori shell. Query params: recipient, tier, send (ISO date or "")
 */

import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GiftCardPreview } from "@/components/GiftCardPreview";
import { MARKETING_URL } from "@/lib/urls";
import { PublicShell } from "@/components/PublicShell";

interface CardSession {
  recipientName: string;
  gifterName: string;
  personalNote: string;
  occasion: 'new_baby' | 'birthday' | 'holiday' | 'general';
  tier: 'keeper' | 'storyteller';
}

function readCardSession(): CardSession | null {
  try {
    const raw = sessionStorage.getItem("mori_gift_card_data");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function GiftSuccessPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const recipient    = params.get("recipient") ?? "your recipient";
  const tierParam    = params.get("tier") ?? "keeper";

  const card = readCardSession();

  const tierDisplay = tierParam.charAt(0).toUpperCase() + tierParam.slice(1);

  return (
    <PublicShell>
    <div className="flex flex-col items-center justify-center flex-1 px-6 py-16 text-center">
      <span className="text-5xl mb-6 block">🌰</span>

      <h1 className="text-2xl font-semibold text-foreground mb-4 leading-snug">
        {t("gift.success.title")}
      </h1>

      <p className="text-base text-muted-foreground leading-relaxed max-w-sm mb-2">
        {t("gift.success.body", { name: card?.recipientName || recipient })}
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
        {t("gift.success.bodyTier", { tier: tierDisplay })}
      </p>

      {/* Downloadable gift card — shown when we have card data from the pre-checkout save */}
      {card && (
        <div className="mt-10 w-full max-w-sm">
          <p className="text-xs text-muted-foreground mb-3 tracking-wide uppercase">
            {t("gift.success.printCard")}
          </p>
          <GiftCardPreview
            recipientName={card.recipientName}
            gifterName={card.gifterName}
            message={card.personalNote}
            occasion={card.occasion}
            tier={card.tier}
            showDownload
          />
        </div>
      )}

      <div className="mt-10 space-y-3">
        <a
          href={MARKETING_URL}
          className="inline-block rounded-xl px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-85"
          style={{ backgroundColor: "#7A9A7C" }}
        >
          {t("gift.backToMori")}
        </a>
        <p className="text-[11px] text-muted-foreground">
          {t("gift.success.confirmation")}
        </p>
      </div>
    </div>
    </PublicShell>
  );
}
